import { useCallback, useEffect, useRef, useState } from 'react';
import WillCardSkeleton from './WillCardSkeleton';
import { getFilteredWills, WillFeedParams, Will } from '../services/api';
import WillCard from './WillCard.tsx';

interface WillGridProps {
  filters?: WillFeedParams;
  onFilterChange?: (filters: WillFeedParams) => void;
  viewMode?: 'grid' | 'list';
}

const mapStatusToUrgency = (status: string, fulfillmentTime?: string): "low" | "medium" | "high" => {
  if (!fulfillmentTime) return "low";
  const dueDate = new Date(fulfillmentTime);
  const today = new Date();
  const oneWeek = new Date();
  oneWeek.setDate(today.getDate() + 7);
  return dueDate <= oneWeek ? "high" : "medium";
};

const currencySymbols: { [key: string]: string } = {
  USD: '$', EUR: '€', ILS: '₪', GBP: '£'
};

const getCurrencySymbol = (currency: string | null): string => {
  if (!currency) return '₪';
  return currencySymbols[currency] || '₪';
};

export const WillGrid: React.FC<WillGridProps> = ({ 
  filters = {}, 
  onFilterChange = () => {},
  viewMode = 'grid'
}) => {
  const [loadedWills, setLoadedWills] = useState<Will[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const prevFiltersRef = useRef<WillFeedParams>(filters);

  useEffect(() => {
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    
    if (filtersChanged) {
      setLoadedWills([]);
      setCursor(null);
      setHasMore(true);
      setInitialLoading(true);
      prevFiltersRef.current = filters;
    }
    
    loadInitialWills();
  }, [filters]);

  useEffect(() => {
    if (loadedWills.length > 0 && hasMore && !loading) {
      const timer = setTimeout(() => {
        loadMoreWills();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [loadedWills]);

  const loadInitialWills = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      const response = await getFilteredWills({
        ...filters,
        size: 9
      });

      if (!response || !response.content) {
        setHasMore(false);
        return;
      }

      const newWills = response.content;
      
      if (newWills.length < 9) {
        setHasMore(false);
      }

      if (newWills.length > 0) {
        setCursor(newWills[newWills.length - 1].createdAt);
        setLoadedWills(newWills);
      }
    } catch (error) {
      console.error('Error loading wills:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadMoreWills = useCallback(async () => {
    if (loading || !hasMore) return;
    
    try {
      setLoading(true);
      const response = await getFilteredWills({
        ...filters,
        cursor,
        size: 20
      });

      if (!response || !response.content) {
        setHasMore(false);
        return;
      }

      const newWills = response.content;
      
      if (newWills.length < 20) {
        setHasMore(false);
      }

      if (newWills.length > 0) {
        setCursor(newWills[newWills.length - 1].createdAt);
        setLoadedWills(prevWills => [...prevWills, ...newWills]);
      }
    } catch (error) {
      console.error('Error loading wills:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, cursor, filters]);

  const observer = useRef<IntersectionObserver>();
  const lastWillElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreWills();
        }
      });
      
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMoreWills]
  );

  const urgencyColors = {
    low: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    high: "bg-red-100 text-red-800"
  };

  return (
    <>
      {initialLoading ? (
        <div className={
          viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" 
            : "flex flex-col space-y-4 max-w-3xl mx-auto"
        }>
          {Array(6).fill(0).map((_, i) => (
            <WillCardSkeleton key={i} viewMode={viewMode} />
          ))}
        </div>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" 
            : "flex flex-col space-y-4 max-w-3xl mx-auto"
        }>
          {loadedWills.map((will, index) => (
            <WillCard 
              key={will.id}
              {...will}
              urgency={mapStatusToUrgency(will.status, will.fulfillmentTime || undefined)}
              reward={will.budget ? `${getCurrencySymbol(will.currency)}${will.budget}` : null}
              className={viewMode === 'grid' ? "h-full" : "w-full"}
              viewMode={viewMode}
              lastElementRef={index === loadedWills.length - 1 ? lastWillElementRef : undefined}
              location={will.location || ''}
              fulfillmentTime={will.fulfillmentTime || ''}
              wanterName={will.wanterName || ''}
              createdAt={will.createdAt}
              media={will.media || []}
            />
          ))}
          
          {loading && !initialLoading && (
            <div className="col-span-full text-center py-4">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            </div>
          )}
          
          {!hasMore && loadedWills.length > 0 && (
            <div className="col-span-full text-center py-4 text-gray-500">No more wants to load</div>
          )}
          
          {!hasMore && loadedWills.length === 0 && (
            <div className="col-span-full text-center py-4 text-gray-500">No wa found</div>
          )}
        </div>
      )}
    </>
  );
};

export default WillGrid;
